'use client';

/**
 * usePaymentProcessor Hook
 *
 * Comprehensive payment processing hook that implements the order-first payment flow.
 * Based on breitling-storefront-next's PaymentView implementation.
 *
 * Payment Flow:
 * 1. Create order from checkout (orderCreate)
 * 2. Initialize transaction (transactionInitialize)
 * 3. Handle additional actions (3DS, redirects)
 * 4. Process transaction completion (transactionProcess)
 * 5. Navigate to confirmation or handle errors
 */

import { checkoutPaths } from '@/lib/paths';
import type { PaymentData, SubmitData, UIElement } from '@adyen/adyen-web';
import { useParams } from 'next/navigation';
import { useCallback, useEffect, useRef, useState } from 'react';

import { OrderAbandonDocument, type OrderAppOrderAbandonMutation } from '@/graphql/orderApp/api';
import {
  TransactionInitializeDocument,
  TransactionProcessDocument,
  type SaleorTransactionInitializeMutation,
  type SaleorTransactionInitializeMutationVariables,
  type SaleorTransactionProcessMutation,
  type SaleorTransactionProcessMutationVariables,
} from '@/graphql/saleor/api';
import { readCampaignCookieFromDocument } from '@/lib/campaignTracker';
import {
  handleOrderCreateErrors,
  hasOrderCreateShoppingBagError,
} from '@/lib/checkout/errors/handleOrderCreateErrors';
import { localizeApiErrors } from '@/lib/checkout/errors/localizeApiErrors';
import { getErrorMessage } from '@/lib/checkout/errors/messages';
import type { GenericErrorHandler } from '@/lib/checkout/errors/types';
import { orderCreate } from '@/lib/checkout/operations';
import {
  buildPaymentReturnUrl,
  getNextSplitPaymentToken,
  handleTransactionResponse,
  isAuthorizedTransactions,
  isSuccessfulTransaction,
  isTokenizablePaymentMethodWithoutPayButton,
  isTransactionWithAdditionalAction,
  resolveTransactionResultCode,
  type INonAdyenGatewayCheckout,
} from '@/lib/checkout/payments/adyen';
import {
  fetchAuthorisedPaymentRedirectUrl,
  isOrderConfirmationRedirect,
  resolveSplitPaymentRedirectUrl,
} from '@/lib/checkout/payments/resolveAuthorisedPaymentRedirect';
import {
  clearSplitPaymentInFlight,
  setSplitPaymentInFlight,
} from '@/lib/checkout/payments/splitPaymentSession';
import { type PaymentType } from '@/lib/checkout/payments/types';
import { orderAppMutate, saleorMutate } from '@/lib/graphql/client';
import {
  captureLastCompletedOrder,
  resolvePaymentTypesForCapture,
  type CaptureLastCompletedOrderOptions,
} from '@/lib/gtm/purchaseTracking.client';
import { devError, devWarn } from '@/lib/utils/devLog';
import { checkoutState } from '@/states/checkout';
import {
  hasSplitPaymentInFlight,
  paymentGatewaysActions,
  paymentGatewaysState,
} from '@/states/paymentGatewaysState';

export type TransactionType = 'FULL' | 'DEPOSIT' | 'BALANCE';

export type GtmCaptureContext = Omit<
  CaptureLastCompletedOrderOptions,
  'transactionId' | 'paymentTypes'
>;

const PAYMENT_IN_PROGRESS_ERROR = 'Payment already in progress';

type UsePaymentProcessorSharedOptions = {
  totalAmount: number;
  currency: string;
  countryCode: string;
  locale: string;
  transactionType?: TransactionType;
  gtmCaptureContext?: GtmCaptureContext;
  onSuccess?: (orderId: string) => void | Promise<void>;
  onError?: (error: string) => void;
  onProcessingStart?: () => void;
  onProcessingEnd?: () => void;
  unexpectedOfflinePaymentError?: string;
  /** Shown when an instalment is authorised but the remaining balance cannot be read. */
  splitPaymentBalanceCheckError?: string;
  /** Refreshes Saleor checkout after CPI mismatch on orderCreate (updated totals). */
  refreshCheckout?: () => Promise<unknown>;
};

/** Checkout payment — creates an order from a Saleor checkout before charging. */
export type UsePaymentProcessorCheckoutOptions = UsePaymentProcessorSharedOptions & {
  /** Saleor checkout ID passed to OrderApp orderCreate / orderAbandon. */
  checkoutId: string;
  orderId?: string | null;
  skipOrderCreate?: false;
  payPerLinkToken?: never;
};

/** Pay-per-link — order already exists; only transactionInitialize runs. */
export type UsePaymentProcessorPayPerLinkOptions = UsePaymentProcessorSharedOptions & {
  /** Existing Saleor order ID — never passed to orderCreate. */
  orderId: string;
  skipOrderCreate: true;
  checkoutId?: never;
  /**
   * Link token from the URL. Pay-per-link payers have no checkout cookie, so it
   * is their proof of order ownership for the post-payment balance check.
   */
  payPerLinkToken?: string;
};

export type UsePaymentProcessorOptions =
  | UsePaymentProcessorCheckoutOptions
  | UsePaymentProcessorPayPerLinkOptions;

export interface PaymentProcessResult {
  success: boolean;
  orderId?: string;
  transactionId?: string;
  error?: string;
  requiresAction?: boolean;
  /** Saleor transaction payload — used after wallet `actions.resolve()` to pick confirmation vs next split. */
  transactionData?: Record<string, unknown> | null;
  paymentType?: PaymentType;
  /** Charged amount (instalment when splitting). */
  amount?: number;
}

type AdyenSubmitActions = { resolve: (r: unknown) => void; reject: () => void };

function paymentFailure(error: string): PaymentProcessResult {
  return { success: false, error };
}

function assignLocation(path: string): void {
  globalThis.location.assign(path);
}

async function settleWalletSubmit({
  result,
  actions,
  handleAction,
  applyAdditionalAction,
  navigateAfterPayment,
  getTransactionAmount,
}: {
  result: PaymentProcessResult;
  actions: AdyenSubmitActions;
  handleAction: UIElement['handleAction'];
  applyAdditionalAction: (
    transactionData: Record<string, unknown> | null,
    handleAction: UIElement['handleAction'] | undefined,
    orderId: string,
    paymentType?: PaymentType
  ) => Promise<void>;
  navigateAfterPayment: (opts: {
    orderId: string;
    transactionData: Record<string, unknown> | null;
    paymentType?: PaymentType;
    amount: number;
  }) => Promise<boolean>;
  getTransactionAmount: () => number;
}): Promise<void> {
  if (!result.success || !result.orderId) {
    actions.reject();
    return;
  }

  actions.resolve({ resultCode: 'Authorised' });

  if (result.requiresAction) {
    await applyAdditionalAction(
      result.transactionData ?? null,
      handleAction,
      result.orderId,
      result.paymentType
    );
    return;
  }

  await navigateAfterPayment({
    orderId: result.orderId,
    transactionData: result.transactionData ?? null,
    paymentType: result.paymentType,
    amount: result.amount ?? getTransactionAmount(),
  });
}

async function resolveChargeOutcome({
  orderId,
  paymentType,
  amount,
  handleAction,
  element,
  initResult,
  transactionId,
  applyAdditionalAction,
  navigateAfterPayment,
  errorHandler,
  releasePaymentLock,
}: {
  orderId: string;
  paymentType?: PaymentType;
  amount: number;
  handleAction?: UIElement['handleAction'];
  element?: UIElement;
  initResult: SaleorTransactionInitializeMutation;
  transactionId?: string;
  applyAdditionalAction: (
    transactionData: Record<string, unknown> | null,
    handleAction: UIElement['handleAction'] | undefined,
    orderId: string,
    paymentType?: PaymentType
  ) => Promise<void>;
  navigateAfterPayment: (opts: {
    orderId: string;
    transactionData: Record<string, unknown> | null;
    paymentType?: PaymentType;
    amount: number;
  }) => Promise<boolean>;
  errorHandler: GenericErrorHandler;
  releasePaymentLock: () => void;
}): Promise<PaymentProcessResult> {
  const transactionData = initResult.transactionInitialize?.data;

  if (isTransactionWithAdditionalAction(initResult.transactionInitialize)) {
    const additionalActionResult = {
      success: true as const,
      orderId,
      transactionId,
      requiresAction: true as const,
      transactionData: transactionData as Record<string, unknown> | null,
      paymentType,
      amount,
    };

    if (isTokenizablePaymentMethodWithoutPayButton(paymentType)) {
      return additionalActionResult;
    }

    await applyAdditionalAction(
      additionalActionResult.transactionData,
      handleAction,
      orderId,
      paymentType
    );
    return additionalActionResult;
  }

  const transaction = initResult.transactionInitialize?.transaction;
  const txEvents = transaction?.events?.map((e) => ({ type: e.type ?? undefined })) ?? [];

  if (isSuccessfulTransaction({ events: txEvents })) {
    const navigation = {
      orderId,
      transactionData: transactionData as Record<string, unknown> | null,
      paymentType,
      amount,
    };

    if (isTokenizablePaymentMethodWithoutPayButton(paymentType)) {
      return {
        success: true,
        ...navigation,
        transactionId,
      };
    }

    await navigateAfterPayment(navigation);
    return {
      success: true,
      orderId,
      transactionId,
    };
  }

  if (isAuthorizedTransactions({ events: txEvents })) {
    errorHandler([
      {
        code: 'PAYMENT_PARTIAL',
        message: 'Payment partially processed. Please try again.',
      },
    ]);
    releasePaymentLock();
    return paymentFailure('Payment partially processed');
  }

  const resultCode =
    (transactionData as { resultCode?: string } | null | undefined)?.resultCode ?? 'Error';
  const refusalReason =
    (transactionData as { refusalReasonCode?: string } | null | undefined)?.refusalReasonCode ??
    'Payment was declined';

  errorHandler([{ code: resultCode, message: refusalReason }], { errorType: 'adyen' });
  element?.setStatus?.('ready');
  releasePaymentLock();
  return paymentFailure(refusalReason);
}

export function usePaymentProcessor({
  checkoutId,
  orderId: initialOrderId,
  payPerLinkToken,
  totalAmount,
  currency,
  countryCode,
  locale,
  transactionType,
  gtmCaptureContext,
  onSuccess,
  onError,
  onProcessingStart,
  onProcessingEnd,
  unexpectedOfflinePaymentError = 'An unexpected error occurred during offline payment processing',
  splitPaymentBalanceCheckError = 'Your payment was received, but we could not load the next payment step. Please refresh the page to continue.',
  refreshCheckout,
  skipOrderCreate = false,
}: UsePaymentProcessorOptions) {
  const params = useParams();
  const lang = params.lang as string;

  // State
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isTransactionSuccessful, setIsTransactionSuccessful] = useState(false);
  const [isNavigatingToSplitPayment, setIsNavigatingToSplitPayment] = useState(false);

  // Refs for tracking component state
  const isMountedRef = useRef(true);
  const mountTimeRef = useRef(Date.now());
  const isProcessingRef = useRef(false);
  const orderIdRef = useRef<string | undefined>(initialOrderId ?? undefined);
  const orderNumberRef = useRef<string | undefined>(undefined);
  const transactionIdRef = useRef<string | undefined>(undefined);
  const isChangedAfterOrderCreatedRef = useRef(false);

  // Keep mutable values in refs so async handlers always read the freshest
  // value (avoids stale closures when props update between renders)
  const totalAmountRef = useRef(totalAmount);
  totalAmountRef.current = totalAmount;

  const countryCodeRef = useRef(countryCode);
  countryCodeRef.current = countryCode;

  const localeRef = useRef(locale);
  localeRef.current = locale;

  const payPerLinkTokenRef = useRef(payPerLinkToken);
  payPerLinkTokenRef.current = payPerLinkToken;

  const currencyRef = useRef(currency);
  currencyRef.current = currency;

  const transactionTypeRef = useRef(transactionType);
  transactionTypeRef.current = transactionType;

  const gtmCaptureContextRef = useRef(gtmCaptureContext);
  gtmCaptureContextRef.current = gtmCaptureContext;

  const refreshCheckoutRef = useRef(refreshCheckout);
  refreshCheckoutRef.current = refreshCheckout;

  const skipOrderCreateRef = useRef(skipOrderCreate);
  skipOrderCreateRef.current = skipOrderCreate;

  useEffect(() => {
    if (initialOrderId) {
      orderIdRef.current = initialOrderId;
    }
  }, [initialOrderId]);

  useEffect(() => {
    if (skipOrderCreate && !initialOrderId) {
      devError('[Payment] pay-per-link requires orderId when skipOrderCreate is true');
    }
    if (!skipOrderCreate && !checkoutId) {
      devError('[Payment] checkout flow requires checkoutId when skipOrderCreate is false');
    }
    if (skipOrderCreate && checkoutId) {
      devWarn('[Payment] checkoutId is ignored for pay-per-link (skipOrderCreate)');
    }
  }, [skipOrderCreate, checkoutId, initialOrderId]);

  const tryAcquirePaymentLock = useCallback((): boolean => {
    if (isProcessingRef.current) {
      return false;
    }
    setIsProcessing(true);
    isProcessingRef.current = true;
    onProcessingStart?.();
    return true;
  }, [onProcessingStart]);

  const releasePaymentLock = useCallback(() => {
    setIsProcessing(false);
    isProcessingRef.current = false;
    onProcessingEnd?.();
  }, [onProcessingEnd]);

  // Properly track mounted state across hot reloads and remounts
  useEffect(() => {
    isMountedRef.current = true;
    mountTimeRef.current = Date.now();
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  /**
   * Error handler
   */
  const errorHandler: GenericErrorHandler = useCallback(
    (errors, opts) => {
      const localized = localizeApiErrors(
        errors.map((error) => ({
          code: error.code ?? 'UNKNOWN',
          message: error.message ?? '',
          field: error.field ?? undefined,
        })),
        locale
      );
      const errorMessage = localized[0]?.message ?? getErrorMessage('errors.paymentFailed', locale);
      setError(errorMessage);
      onError?.(errorMessage);
      devError('[Payment] Error:', errors, opts);
    },
    [locale, onError]
  );

  /**
   * Get transaction amount (handles split payment partial payments).
   * Reads from ref to guarantee the freshest value even when called from
   * async handlers before React has re-rendered.
   */
  const getTransactionAmount = useCallback(() => {
    if (paymentGatewaysState.isSplitPayment && paymentGatewaysState.partialAmount) {
      return paymentGatewaysState.partialAmount;
    }
    return totalAmountRef.current;
  }, []);

  /**
   * Get current URL with order ID for return handling
   */
  const getReturnUrl = useCallback((orderId: string) => {
    return buildPaymentReturnUrl(globalThis.location.href, orderId);
  }, []);

  /**
   * Capture checkout snapshot for GTM purchase tracking on the confirmation page.
   * Uses Saleor order ID for dedupe; business ID for transaction_id when available.
   */
  const captureOrderForGtm = useCallback((orderId: string) => {
    const transactionId = orderNumberRef.current;
    const ctx = gtmCaptureContextRef.current;
    captureLastCompletedOrder(orderId, {
      ...(transactionId ? { transactionId } : {}),
      transactionType: transactionTypeRef.current,
      paymentTypes: resolvePaymentTypesForCapture(),
      ...ctx,
      chargedAmount: ctx?.chargedAmount ?? totalAmountRef.current,
    });
  }, []);

  /**
   * Create order from checkout
   */
  const createOrder = useCallback(
    async (checkoutIdToUse: string): Promise<string | null> => {
      try {
        const result = await orderCreate({
          checkoutId: checkoutIdToUse,
          campaignData: readCampaignCookieFromDocument(),
        });

        if (result.errors.length > 0) {
          const { localizedErrors } = await handleOrderCreateErrors({
            errors: result.errors,
            locale,
            refreshCheckout: refreshCheckoutRef.current,
          });

          if (hasOrderCreateShoppingBagError(result.errors)) {
            return null;
          }

          errorHandler(
            localizedErrors.map((e) => ({
              code: e.code,
              message: e.message ?? 'Failed to create order',
              field: e.field ?? undefined,
            })),
            { errorType: 'orderApp' }
          );
          return null;
        }

        const newOrderId = result.orderId;
        if (newOrderId) {
          orderIdRef.current = newOrderId;
          orderNumberRef.current = result.orderNumber ?? undefined;
          checkoutState.orderId = newOrderId;
          isChangedAfterOrderCreatedRef.current = false;
          if (paymentGatewaysState.isSplitPayment) {
            setSplitPaymentInFlight(newOrderId);
          }
          return newOrderId;
        }

        errorHandler([{ code: 'ORDER_CREATE_FAILED', message: 'Failed to create order' }]);
        return null;
      } catch (err) {
        errorHandler([
          {
            code: 'ORDER_CREATE_ERROR',
            message: err instanceof Error ? err.message : 'Order creation failed',
          },
        ]);
        return null;
      }
    },
    [errorHandler, locale]
  );

  /**
   * Abandon order if checkout was modified after order creation
   */
  const abandonOrder = useCallback(
    async (checkoutIdToUse: string, orderIdToAbandon: string): Promise<boolean> => {
      try {
        // oxlint-disable-next-line typescript/no-explicit-any
        const result = await orderAppMutate<OrderAppOrderAbandonMutation, any>(
          OrderAbandonDocument,
          {
            checkoutId: checkoutIdToUse,
            orderId: orderIdToAbandon,
          }
        );

        if (result?.orderAbandon?.errors?.length) {
          const errors = result.orderAbandon.errors;
          errorHandler(
            errors.map((e) => ({
              code: e.code,
              message: e.message,
              field: e.field ?? undefined,
            })),
            { errorType: 'orderApp' }
          );
          return false;
        }

        // Clear the old order ID
        orderIdRef.current = undefined;
        checkoutState.orderId = null;
        return true;
      } catch (err) {
        errorHandler([
          {
            code: 'ORDER_ABANDON_ERROR',
            message: err instanceof Error ? err.message : 'Failed to abandon order',
          },
        ]);
        return false;
      }
    },
    [errorHandler]
  );

  const ensureCheckoutOrder = useCallback(
    async (
      currentMountTime: number
    ): Promise<{ ok: true; orderId: string } | { ok: false; error: string }> => {
      if (skipOrderCreateRef.current) {
        if (!orderIdRef.current) {
          errorHandler([{ code: 'ORDER_MISSING', message: 'Order is required for payment' }]);
          return { ok: false, error: 'Order is required for payment' };
        }
        return { ok: true, orderId: orderIdRef.current };
      }

      const shouldAbandonOrder =
        isChangedAfterOrderCreatedRef.current && Boolean(orderIdRef.current);
      const needsNewOrder = !orderIdRef.current || shouldAbandonOrder;

      if (!needsNewOrder) {
        return { ok: true, orderId: orderIdRef.current! };
      }

      if (!checkoutId) {
        errorHandler([{ code: 'CHECKOUT_MISSING', message: 'Checkout ID is required' }]);
        return { ok: false, error: 'Checkout ID is required' };
      }

      if (shouldAbandonOrder && orderIdRef.current) {
        const abandoned = await abandonOrder(checkoutId, orderIdRef.current);
        if (!abandoned) {
          return { ok: false, error: 'Failed to abandon existing order' };
        }
      }

      const newOrderId = await createOrder(checkoutId);
      if (!newOrderId) {
        return { ok: false, error: 'Failed to create order' };
      }

      if (!isMountedRef.current || currentMountTime !== mountTimeRef.current) {
        return { ok: false, error: 'Component state changed during processing' };
      }

      return { ok: true, orderId: newOrderId };
    },
    [abandonOrder, checkoutId, createOrder, errorHandler]
  );

  /**
   * Initialize transaction with Saleor
   */
  const initializeTransaction = useCallback(
    async ({
      orderId,
      amount,
      gatewayId,
      paymentData,
      returnUrl,
      type = 'FULL',
      cardBin,
    }: {
      orderId: string;
      amount: number;
      gatewayId?: string;
      paymentData: PaymentData;
      returnUrl: string;
      type?: TransactionType;
      cardBin?: string;
    }) => {
      // Get origin for PayPal and other redirect-based payments
      const origin = globalThis.location?.origin ?? '';

      const result = await saleorMutate<
        SaleorTransactionInitializeMutation,
        SaleorTransactionInitializeMutationVariables
      >(TransactionInitializeDocument, {
        id: orderId,
        amount: amount,
        gatewayId: gatewayId ?? paymentGatewaysState.configs[0]?.id ?? '',
        data: {
          ...paymentData,
          origin, // Required for PayPal and redirect-based payments
          returnUrl,
          countryCode,
          shopperLocale: locale,
          shopperIP: '',
          action: 'AUTHORIZE', // Must be 'AUTHORIZE' not 'AUTHORIZATION' per Adyen Saleor App schema
          type,
          ...(cardBin ? { cardBin } : {}),
        },
      });

      return result;
    },
    [countryCode, locale]
  );

  /**
   * Process 3DS/additional details
   */
  const processTransaction = useCallback(
    async (transactionId: string, additionalData: Record<string, unknown>) => {
      const result = await saleorMutate<
        SaleorTransactionProcessMutation,
        SaleorTransactionProcessMutationVariables
      >(TransactionProcessDocument, {
        id: transactionId,
        data: additionalData,
      });

      return result;
    },
    []
  );

  /**
   * Handle successful payment navigation.
   * Uses hard navigation (`globalThis.location.assign`) instead of router.push
   * because client-side navigation can be interrupted by Adyen SDK lifecycle
   * and React re-renders after payment processing.
   */
  const navigateToConfirmation = useCallback(
    (orderId: string) => {
      captureOrderForGtm(orderId);
      assignLocation(`/${lang}${checkoutPaths.confirmation(orderId)}`);
    },
    [captureOrderForGtm, lang]
  );

  /**
   * Unified post-payment navigation for both direct and 3DS paths.
   * Resolves the redirect destination (confirmation vs next split payment),
   * fires side effects (onSuccess, payment record, tracking), and navigates.
   *
   * Returns `true` when navigation was initiated (caller should keep spinner).
   * Returns `false` when an unrecoverable error occurred (caller should clear state).
   */
  const navigateAfterPayment = useCallback(
    async (opts: {
      orderId: string;
      transactionData: Record<string, unknown> | null;
      paymentType?: PaymentType;
      amount: number;
    }): Promise<boolean> => {
      const { orderId, transactionData, paymentType, amount } = opts;

      setIsTransactionSuccessful(true);

      let redirectUrl: string;
      try {
        redirectUrl = await fetchAuthorisedPaymentRedirectUrl({
          orderId,
          checkoutId,
          payPerLinkToken: payPerLinkTokenRef.current,
          totalAmount: totalAmountRef.current,
          countryCode: countryCodeRef.current,
          locale: localeRef.current,
          transactionData,
        });
      } catch (err) {
        devError('[Payment] Balance check failed:', err);

        // A split payment may still owe money, and confirmation would present a
        // partially paid order as complete (BEC-24895). Keep the shopper on the
        // payment page so the remaining instalments stay reachable.
        if (hasSplitPaymentInFlight(orderId)) {
          setIsTransactionSuccessful(false);
          setError(splitPaymentBalanceCheckError);
          onError?.(splitPaymentBalanceCheckError);
          releasePaymentLock();
          return false;
        }

        redirectUrl = checkoutPaths.confirmation(orderId);
      }

      if (isOrderConfirmationRedirect(orderId, redirectUrl)) {
        clearSplitPaymentInFlight();
        await onSuccess?.(orderId);
        captureOrderForGtm(orderId);
        assignLocation(`/${lang}${redirectUrl}`);
      } else {
        if (paymentGatewaysState.isSplitPayment) {
          paymentGatewaysActions.addPaymentRecord({
            id: transactionIdRef.current || `payment-${Date.now()}`,
            type: paymentType || 'card',
            amount,
            currency: currencyRef.current,
            transactionId: transactionIdRef.current,
            status: 'authorized',
            timestamp: new Date().toISOString(),
          });
        }
        setIsNavigatingToSplitPayment(true);
        assignLocation(`/${lang}${redirectUrl}`);
      }

      return true;
    },
    [
      captureOrderForGtm,
      checkoutId,
      lang,
      onError,
      onSuccess,
      releasePaymentLock,
      splitPaymentBalanceCheckError,
    ]
  );

  const applyAdditionalAction = useCallback(
    async (
      transactionData: Record<string, unknown> | null,
      handleAction: UIElement['handleAction'] | undefined,
      orderId: string,
      paymentType?: PaymentType
    ) => {
      // An authorised payload carries no action to apply, and confirming here
      // would skip the split payment balance check (BEC-24895).
      if (resolveTransactionResultCode(transactionData) === 'Authorised') {
        await navigateAfterPayment({
          orderId,
          transactionData,
          paymentType,
          amount: getTransactionAmount(),
        });
        return;
      }

      await handleTransactionResponse(transactionData, {
        handleAction,
        errorHandler,
        paymentType,
        setIsTransactionSuccessfully: setIsTransactionSuccessful,
      });
    },
    [errorHandler, getTransactionAmount, navigateAfterPayment]
  );

  /**
   * Charge the selected method. Cards navigate here on success. Google Pay /
   * Apple Pay return `transactionData`, `paymentType`, and `amount` instead so
   * `createOnSubmitHandler` can `actions.resolve()` before leaving the page.
   */
  const handlePayment = useCallback(
    async ({
      gatewayId,
      paymentType,
      paymentData,
      handleAction,
      element,
      cardBin,
    }: {
      gatewayId?: string;
      paymentType?: PaymentType;
      paymentData: PaymentData;
      handleAction?: UIElement['handleAction'];
      element?: UIElement;
      cardBin?: string;
    }): Promise<PaymentProcessResult> => {
      if (!tryAcquirePaymentLock()) {
        return paymentFailure(PAYMENT_IN_PROGRESS_ERROR);
      }

      if (!isMountedRef.current) {
        releasePaymentLock();
        return paymentFailure('Component unmounted');
      }

      const prepared = await ensureCheckoutOrder(mountTimeRef.current);
      if (!prepared.ok) {
        releasePaymentLock();
        return paymentFailure(prepared.error);
      }

      const { orderId } = prepared;
      const amount = getTransactionAmount();
      const initResult = await initializeTransaction({
        orderId,
        amount,
        gatewayId,
        paymentData,
        returnUrl: getReturnUrl(orderId),
        type: transactionTypeRef.current ?? 'FULL',
        cardBin,
      });

      const transactionErrors = initResult.transactionInitialize?.errors ?? [];
      if (transactionErrors.length) {
        errorHandler(
          transactionErrors.map((e) => ({
            code: e.code,
            message: e.message ?? undefined,
            field: e.field ?? undefined,
          }))
        );
        releasePaymentLock();
        return paymentFailure(transactionErrors[0]?.message ?? 'Transaction failed');
      }

      transactionIdRef.current = initResult.transactionInitialize?.transaction?.id;

      return resolveChargeOutcome({
        orderId,
        paymentType,
        amount,
        handleAction,
        element,
        initResult,
        transactionId: transactionIdRef.current,
        applyAdditionalAction,
        navigateAfterPayment,
        errorHandler,
        releasePaymentLock,
      });
    },
    [
      ensureCheckoutOrder,
      initializeTransaction,
      getTransactionAmount,
      getReturnUrl,
      errorHandler,
      navigateAfterPayment,
      applyAdditionalAction,
      tryAcquirePaymentLock,
      releasePaymentLock,
    ]
  );

  /**
   * Handle 3DS additional details callback
   */
  const handleAdditionalDetails = useCallback(
    async (state: Record<string, unknown>, element: UIElement) => {
      setIsProcessing(true);
      isProcessingRef.current = true;
      onProcessingStart?.();

      // Track if we're navigating to avoid clearing spinner too early
      let isNavigating = false;

      try {
        if (!isMountedRef.current || !transactionIdRef.current) {
          return;
        }

        const currentMountTime = mountTimeRef.current;
        const result = await processTransaction(
          transactionIdRef.current,
          state.data as Record<string, unknown>
        );

        if (!isMountedRef.current || currentMountTime !== mountTimeRef.current) {
          return;
        }

        const transactionData = result.transactionProcess?.data;
        const orderId = orderIdRef.current;

        if (!orderId) {
          errorHandler([{ code: 'ORDER_MISSING', message: 'Order is required for payment' }]);
          return;
        }

        if (transactionData) {
          // Check if this is a successful authorization
          const resultCode = (transactionData as Record<string, unknown>)?.resultCode;

          if (resultCode === 'Authorised') {
            isNavigating = true;
            await navigateAfterPayment({
              orderId,
              transactionData: transactionData as Record<string, unknown>,
              amount: getTransactionAmount(),
            });
            return;
          }

          // For non-Authorised results, use the standard flow
          const payPerLinkToken = getNextSplitPaymentToken(transactionData) ?? null;
          const redirectUrl = resolveSplitPaymentRedirectUrl({
            orderId,
            amountRemainingToBePaid: Boolean(payPerLinkToken),
            payPerLinkToken,
          });

          const navigationResult = await handleTransactionResponse(
            transactionData as Record<string, unknown>,
            {
              handleAction: element.handleAction,
              push: async (url) => {
                isNavigating = true;
                assignLocation(`/${lang}${url}`);
                return true;
              },
              redirectPaths: {
                Authorised: redirectUrl,
              },
              errorHandler,
              setIsTransactionSuccessfully: setIsTransactionSuccessful,
            }
          );

          // If navigation started, keep spinner visible
          if (navigationResult === true || isNavigating) {
            return; // Don't clear processing state - let the page transition handle it
          }
        } else {
          errorHandler([{ code: 'Error', message: 'Transaction processing failed' }], {
            errorType: 'adyen',
          });
          setIsTransactionSuccessful(false);
        }
      } finally {
        // Only clear processing if we're not navigating
        if (!isNavigating) {
          setIsProcessing(false);
          isProcessingRef.current = false;
          onProcessingEnd?.();
        }
      }
    },
    [
      processTransaction,
      errorHandler,
      lang,
      navigateAfterPayment,
      getTransactionAmount,
      onProcessingStart,
      onProcessingEnd,
    ]
  );

  /**
   * Process non-Adyen payment (financing, bank transfer)
   */
  const processNonAdyenPayment = useCallback(
    async (
      gatewayCheckout: INonAdyenGatewayCheckout,
      paymentMethod: { type: PaymentType; gateway?: string; onBeforeCharge?: () => void }
    ): Promise<boolean> => {
      setError(null);

      try {
        if (!gatewayCheckout.isValid()) {
          if (!gatewayCheckout.hasInlineValidationError()) {
            errorHandler([
              { code: 'VALIDATION_ERROR', message: 'Payment method validation failed' },
            ]);
          }
          return false;
        }

        // V12, CreditPlus, and Agos redirect inside handlePayment. Start GTM first.
        paymentMethod.onBeforeCharge?.();

        const state = gatewayCheckout.getState();
        const paymentData: PaymentData = {
          paymentMethod: {
            type: paymentMethod.type,
          },
          clientStateDataIndicator: true,
          ...state,
        };

        const result = await handlePayment({
          gatewayId: paymentMethod.gateway,
          paymentType: paymentMethod.type,
          paymentData,
        });

        return result.success === true;
      } catch (err) {
        errorHandler([
          {
            code: 'PAYMENT_ERROR',
            message: err instanceof Error ? err.message : 'Payment processing failed',
          },
        ]);
        return false;
      }
    },
    [handlePayment, errorHandler]
  );

  /**
   * Adyen `onSubmit`. Wallets: resolve the native sheet, then navigate with the
   * `handlePayment` result. Failure calls `actions.reject()`.
   */
  const createOnSubmitHandler = useCallback(
    (gatewayId: string, options?: { getCardBin?: () => string | null }) =>
      async (
        state: SubmitData,
        element: UIElement,
        actions: { resolve: (r: unknown) => void; reject: () => void }
      ) => {
        setError(null);

        const paymentMethodType = (state as { data?: { paymentMethod?: { type?: string } } }).data
          ?.paymentMethod?.type;

        // Check if form is valid - Adyen should only call onSubmit when valid
        // but we log it just in case
        if (!(state as { isValid?: boolean }).isValid) {
          devWarn('[Payment] state.isValid is false, showing validation');
          actions.reject();
          element.showValidation?.();
          return;
        }

        const handleAction: UIElement['handleAction'] = (...props) => {
          return element.handleAction(...props);
        };

        const isWallet = isTokenizablePaymentMethodWithoutPayButton(paymentMethodType);

        try {
          const result = await handlePayment({
            gatewayId,
            paymentType: paymentMethodType as PaymentType,
            paymentData: state.data,
            handleAction,
            element,
            cardBin: options?.getCardBin?.() ?? undefined,
          });

          if (isWallet) {
            await settleWalletSubmit({
              result,
              actions,
              handleAction,
              applyAdditionalAction,
              navigateAfterPayment,
              getTransactionAmount,
            });
          }

          const paymentFailed = !result.success && result.error !== PAYMENT_IN_PROGRESS_ERROR;
          if (paymentFailed || result.requiresAction) {
            releasePaymentLock();
          }
        } catch (err) {
          devError('[Payment] handlePayment threw error:', err);
          const message = err instanceof Error ? err.message : 'Payment processing failed';
          errorHandler([{ code: 'PAYMENT_ERROR', message }]);
          releasePaymentLock();
          if (isWallet) {
            actions.reject();
          }
        }
      },
    [
      handlePayment,
      errorHandler,
      releasePaymentLock,
      navigateAfterPayment,
      applyAdditionalAction,
      getTransactionAmount,
    ]
  );

  /**
   * Adyen `onError`. Always releases the processing lock. Wallet-sheet CANCEL
   * is not shown as a payment error.
   */
  const handleAdyenError = useCallback(
    async (error: Error) => {
      releasePaymentLock();
      if (error.name !== 'CANCEL') {
        devError('[Payment] Adyen error:', error);
        errorHandler([{ code: 'PAYMENT_ERROR', message: error.message }]);
      }
    },
    [errorHandler, releasePaymentLock]
  );

  const handleOfflinePayment = useCallback(async (): Promise<PaymentProcessResult> => {
    setError(null);

    const entityId = orderIdRef.current ?? checkoutId ?? paymentGatewaysState.id;

    // Bank transfer settles outside Adyen, so this path confirms the order
    // without a balance check — which would mark an instalment plan complete.
    // The UI already hides the method while splitting; this is the last guard.
    if (hasSplitPaymentInFlight(entityId)) {
      devError('[Payment] Offline payment attempted during a split payment');
      setError(unexpectedOfflinePaymentError);
      onError?.(unexpectedOfflinePaymentError);
      return { success: false, error: unexpectedOfflinePaymentError };
    }

    if (!tryAcquirePaymentLock()) {
      return { success: false, error: PAYMENT_IN_PROGRESS_ERROR };
    }

    try {
      const prepared = await ensureCheckoutOrder(mountTimeRef.current);
      if (!prepared.ok) {
        return { success: false, error: prepared.error };
      }

      const { orderId } = prepared;

      const toggleRes = await fetch('/api/payment/offline-toggle', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ orderId, payPerLinkToken: payPerLinkTokenRef.current || undefined }),
      });
      const body = await toggleRes.json();

      if (!toggleRes.ok || !body.success) {
        const message = body.errors?.[0]?.message ?? 'Payment failed';
        errorHandler([{ code: 'OFFLINE_TOGGLE_ERROR', message }], { errorType: 'orderApp' });
        return { success: false, error: message };
      }

      await onSuccess?.(orderId);
      navigateToConfirmation(orderId);
      return { success: true, orderId };
    } catch {
      const message =
        unexpectedOfflinePaymentError ?? getErrorMessage('errors.paymentFailed', locale);
      setError(message);
      onError?.(message);
      return { success: false, error: message };
    } finally {
      releasePaymentLock();
    }
  }, [
    ensureCheckoutOrder,
    errorHandler,
    navigateToConfirmation,
    onSuccess,
    tryAcquirePaymentLock,
    releasePaymentLock,
    unexpectedOfflinePaymentError,
    locale,
    onError,
  ]);

  /**
   * Cleanup on unmount
   */
  const cleanup = useCallback(() => {
    isMountedRef.current = false;
  }, []);

  return {
    isProcessing,
    error,
    isTransactionSuccessful,
    isNavigatingToSplitPayment,
    handlePayment,
    handleAdditionalDetails,
    processNonAdyenPayment,
    createOnSubmitHandler,
    handleAdyenError,
    handleOfflinePayment,
    cleanup,
    releasePaymentLock,
    // Expose for external use
    createOrder,
    abandonOrder,
    orderIdRef,
    transactionIdRef,
    isChangedAfterOrderCreatedRef,
  };
}
